import type { PropsWithChildren } from 'react';
import { style } from 'typestyle';
import {
    MetabaseProvider as MetabaseSdkProvider,
    defineMetabaseAuthConfig,
    type MetabaseAuthConfig,
    type SdkErrorComponentProps,
} from '@metabase/embedding-sdk-react';
import { METABASE_SITE_URL } from '@src/config';
import { ADALONG_COLORS } from '@src/styles';
import { metabaseTheme } from './metabaseTheme';

// `METABASE_SITE_URL` is injected at build time through `__GLOBAL__` (untyped).
// Normalize it to a plain string for the SDK. The provider refuses to mount
// when the value is missing (see `AnalyzeMetabaseProvider` below).
const METABASE_URL: string = typeof METABASE_SITE_URL === 'string' && METABASE_SITE_URL
    ? METABASE_SITE_URL
    : '';

/**
 * Guest authentication is configured at the provider level. Each
 * `StaticDashboard` receives its short-lived, dashboard-scoped Guest token
 * from the authenticated Adalong API response.
 */
const authConfig: MetabaseAuthConfig = defineMetabaseAuthConfig({
    metabaseInstanceUrl: METABASE_URL,
    isGuest: true,
});

const stateClass = style({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 200,
    padding: 24,
    color: ADALONG_COLORS.LIGHT_GRAY,
    fontSize: 14,
    textAlign: 'center',
});

function SdkLoader({ label }: { label?: string }) {
    return (
        <div role="status" className={stateClass} aria-live="polite">
            {label ?? 'Loading dashboard…'}
        </div>
    );
}

function SdkError({ message, error }: SdkErrorComponentProps) {
    const text = typeof message === 'string' && message
        ? message
        : error?.message
            ? error.message
            : 'Unable to load the Metabase dashboard. Please check your connection.';

    return (
        <div role="alert" className={stateClass} aria-live="assertive">
            {text}
        </div>
    );
}

export function AnalyzeMetabaseProvider({ children }: PropsWithChildren) {
    // `METABASE_SITE_URL` is a public build-time URL. If it is missing the
    // SDK cannot be initialized at all, so render an actionable state instead
    // of letting the SDK fail with an opaque error.
    if (!METABASE_URL) {
        return (
            <div role="alert" className={stateClass} aria-live="assertive">
                The Metabase analytics service is not configured for this build.
            </div>
        );
    }

    return (
        <MetabaseSdkProvider
            authConfig={authConfig}
            loaderComponent={SdkLoader}
            errorComponent={SdkError}
            locale="en"
            theme={metabaseTheme}
        >
            {children}
        </MetabaseSdkProvider>
    );
}
